import React, { useState } from 'react';
import { Package, Truck, MapPin, Clock, CheckCircle2, Phone, Mail, ArrowRight, Globe, Shield, DollarSign, Zap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const InternationalCourierTorontoPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    parcelType: ''
  });
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Quote Request Received!",
      description: "Our Toronto team will contact you within 24 hours with competitive courier rates."
    });
    setFormData({ name: '', email: '', phone: '', parcelType: '' });
  };

  const benefits = [
    {
      icon: MapPin,
      title: "Toronto & GTA Coverage",
      description: "Comprehensive courier services throughout Toronto and the Greater Toronto Area with multiple pickup locations and same-day collection options."
    },
    {
      icon: Zap,
      title: "Fast International Delivery",
      description: "Express courier services to India, Pakistan, UK, USA, and 185+ countries with delivery times as fast as 3-5 business days."
    },
    {
      icon: DollarSign,
      title: "Competitive Toronto Rates",
      description: "We compare DHL, FedEx, UPS, and other trusted carriers to secure the best courier rates for Toronto customers. Save up to 75% on shipping."
    },
    {
      icon: Truck,
      title: "Reliable Carrier Partners",
      description: "Partner exclusively with established international carriers known for timely delivery, safe handling, and global tracking capabilities."
    },
    {
      icon: Shield,
      title: "Zero Hassle Customs",
      description: "Complete customs documentation and clearance support to ensure your parcels clear customs smoothly without delays or complications."
    },
    {
      icon: Globe,
      title: "GTA Relocation Experts",
      description: "Specialized services for large relocation shipments from Toronto to anywhere in the world with tailored packaging and handling."
    }
  ];

  const steps = [
    {
      number: 1,
      title: "Request a Quote",
      description: "Contact Shippzy online, by phone, or visit one of our partner locations throughout Toronto and the GTA."
    },
    {
      number: 2,
      title: "Compare Carrier Rates",
      description: "We assess your parcel's weight and dimensions, then compare rates from DHL, FedEx, UPS, and other carriers to find the best deal."
    },
    {
      number: 3,
      title: "Book Your Shipment",
      description: "Choose your preferred carrier and service level. We handle all the paperwork, customs forms, and shipping labels."
    },
    {
      number: 4,
      title: "Real-Time Tracking",
      description: "Track your parcel's journey from Toronto to its international destination with live updates sent directly to your email."
    },
    {
      number: 5,
      title: "Safe Delivery",
      description: "Your parcel is delivered to the recipient's door with signature confirmation and proof of delivery."
    }
  ];

  const shippableItems = [
    'Business documents and contracts',
    'Gifts and personal parcels',
    'Electronics and tech devices',
    'Apparel and fashion items',
    'Educational books and materials',
    'Relocation and household goods',
    'Food items (non-perishable)',
    'Jewelry and accessories'
  ];

  const faqs = [
    {
      question: "What are the rates for international courier from Toronto?",
      answer: "Courier rates from Toronto vary based on parcel weight, dimensions, destination country, and carrier selection. Shippzy compares rates from DHL, FedEx, UPS, and other carriers to provide you with the most competitive pricing. For large relocation shipments over 100kg, we offer special rates from $7.99/kg. Contact us for an accurate quote based on your specific shipment."
    },
    {
      question: "How quickly can parcels be shipped from Toronto internationally?",
      answer: "Delivery times depend on the destination and service selected. Express courier services to India typically take 3-7 business days, while standard shipping may take 7-15 days. Shipments to the USA can arrive in 1-3 days, Europe in 3-7 days, and other destinations vary. We provide estimated delivery dates when you book."
    },
    {
      question: "Can I ship to India from Toronto?",
      answer: "Yes! We specialize in courier services from Toronto to India with door-to-door delivery, customs clearance support, and competitive rates. Toronto has a large Indian community, and we understand the unique needs of this route. Whether you're sending gifts, documents, or relocation items, we ensure fast and reliable delivery to any city in India."
    },
    {
      question: "Which courier companies does Shippzy work with in Toronto?",
      answer: "Shippzy partners with major international courier companies including DHL, FedEx, UPS, and Purolator. By comparing rates across multiple carriers, we ensure Toronto customers always get the best combination of price, speed, and service quality for their international shipments."
    },
    {
      question: "Where can I drop off my parcel in Toronto?",
      answer: "Shippzy has multiple partner drop-off locations throughout Toronto and the GTA for maximum convenience. We also offer pickup services from your home, office, or business. Contact our team to find the nearest drop-off location or schedule a pickup at your preferred time."
    },
    {
      question: "Does Shippzy assist with international customs clearance?",
      answer: "Absolutely! We provide full customs documentation support and work with experienced customs brokers to ensure smooth clearance at the destination. We guide you through the entire process and help prepare all necessary paperwork. Note that any customs duties or taxes levied by the destination country are the responsibility of the sender or recipient."
    },
    {
      question: "Can I track my international courier shipment from Toronto?",
      answer: "Yes, all shipments include comprehensive tracking. Once your parcel is dispatched from Toronto, you'll receive a tracking number via email that allows you to monitor its journey in real time until it reaches the recipient."
    },
    {
      question: "What items cannot be shipped internationally from Toronto?",
      answer: "Prohibited items for international shipping include liquids, flammable substances, weapons, explosives, perishable foods, live plants or animals, cash, and certain electronics with large lithium batteries. Restrictions can vary by destination country. Please contact us for a detailed list of prohibited and restricted items before shipping."
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>International Courier Toronto | Fast Shipping Services – Shippzy</title>
        <meta name="description" content="International courier services from Toronto. Ship to India and 185+ countries. Compare DHL, FedEx, UPS rates. Fast delivery, customs clearance support, and convenient GTA pickup. Save up to 75% on parcel shipping." />
        <meta name="keywords" content="international courier Toronto, international shipping Toronto, shipping to India from Toronto, cheap courier Toronto, parcel shipping Toronto, courier services Toronto, Toronto courier rates, ship from Toronto" />
        <link rel="canonical" href="https://www.shippzy.ca/international-courier-toronto" />
        
        {/* Open Graph */}
        <meta property="og:title" content="International Courier Toronto | Fast Shipping – Shippzy" />
        <meta property="og:description" content="Ship internationally from Toronto. Compare DHL, FedEx, UPS rates. Fast courier service with customs support and GTA pickup." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.shippzy.ca/international-courier-toronto" />
        
        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "International Courier from Toronto",
            "provider": {
              "@type": "Organization",
              "name": "Shippzy Inc.",
              "telephone": "+1-437-616-0758",
              "email": "ship@shippzy.ca",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "140 Advance Blvd, Unit 7",
                "addressLocality": "Brampton",
                "addressRegion": "ON",
                "postalCode": "L6T 4Z8",
                "addressCountry": "CA"
              }
            },
            "areaServed": {
              "@type": "City",
              "name": "Toronto",
              "containedIn": {
                "@type": "State",
                "name": "Ontario"
              }
            },
            "serviceType": "International Courier Service",
            "offers": {
              "@type": "Offer",
              "priceSpecification": {
                "@type": "PriceSpecification",
                "price": "9.99",
                "priceCurrency": "CAD",
                "unitText": "per kg",
                "minWeight": "23 kg minimum"
              }
            }
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div className="inline-block bg-gradient-to-r from-red-600 to-yellow-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                🚀 Serving Toronto & GTA
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                International Courier Services in Toronto
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Shippzy delivers fast and affordable international courier services from Toronto to India and over 185 countries worldwide. We work with trusted carriers like DHL, FedEx, and UPS to compare rates and provide you with the best value and fastest delivery options. Whether you need to send urgent business documents, personal gifts, or large relocation shipments, our Toronto-based team offers door-to-door service, expert customs clearance assistance, and convenient pickup throughout the GTA.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-6 text-lg"
                  onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Free Quote
                </Button>
                <Button 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg"
                  onClick={() => window.location.href = 'tel:+14376160758'}
                >
                  Call: +1 (437) 616-0758
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Shippzy Toronto Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Toronto Customers Choose Shippzy</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Trusted by thousands of Toronto residents and businesses for international courier services that are reliable, fast, and affordable.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="pt-6">
                      <div className="w-14 h-14 bg-gradient-to-r from-red-600 to-yellow-500 rounded-lg flex items-center justify-center mb-4">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">How Our Courier Service Works from Toronto</h2>
              <p className="text-xl text-gray-600">5 simple steps to ship your parcels internationally from Toronto</p>
            </div>

            <div className="space-y-6">
              {steps.map((step, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex gap-6 items-start">
                      <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl font-bold text-white">{step.number}</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Can You Ship Section */}
        <section className="py-20 bg-gradient-to-br from-red-50 to-yellow-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What Can You Ship Internationally from Toronto</h2>
              <p className="text-xl text-gray-600">
                We handle diverse shipment types for personal, business, and relocation needs
              </p>
            </div>

            <Card className="shadow-xl">
              <CardContent className="pt-8">
                <div className="grid md:grid-cols-2 gap-4">
                  {shippableItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                      <span className="text-gray-800">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8 p-4 bg-yellow-100 border-l-4 border-yellow-500 rounded">
                  <p className="text-sm text-gray-800">
                    <strong>Note:</strong> Some items are restricted or prohibited for international courier shipping. Contact our Toronto team for a comprehensive list of restricted items based on your destination country.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Popular Destinations Section */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Ship from Toronto to Anywhere in the World</h2>
              <p className="text-xl text-gray-600">
                Top destinations for Toronto international courier services
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {['India', 'Pakistan', 'United Kingdom', 'United States', 'China', 'Philippines', 'Jamaica', 'United Arab Emirates'].map((country, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Globe className="w-8 h-8 text-red-600 mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-gray-900">{country}</h3>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-8">
              <p className="text-gray-600">
                Need to ship to a different country? <a href="#quote-form" className="text-red-600 hover:text-red-700 font-semibold underline">Contact us</a> – we serve 185+ countries worldwide!
              </p>
            </div>

            <div className="mt-12 bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Related Services</h3>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="/shipping-to-india-from-canada" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Shipping to India from Canada
                </a>
                <span className="text-gray-400">•</span>
                <a href="/cheap-shipping-canada-to-india" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Cheap shipping options from Canada to India
                </a>
                <span className="text-gray-400">•</span>
                <a href="/relocation-shipping-canada-to-india" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Relocation shipping to India
                </a>
                <span className="text-gray-400">•</span>
                <a href="/international-shipping-brampton" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Brampton shipping services
                </a>
                <span className="text-gray-400">•</span>
                <a href="/partner" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Find partner locations
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Common questions about international courier services from Toronto</p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors cursor-pointer" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <ArrowRight className={`w-5 h-5 text-red-600 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {openFaq === index && (
                      <p className="text-gray-600 mt-4 leading-relaxed">{faq.answer}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA / Quote Form Section */}
        <section id="quote-form" className="py-20 bg-gradient-to-r from-red-600 to-yellow-500">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white mb-8">
              <h2 className="text-4xl font-bold mb-4">Get Toronto Courier Rates Now</h2>
              <p className="text-xl">Free quote with no obligation – compare and save</p>
            </div>

            <Card className="shadow-2xl">
              <CardHeader>
                <CardTitle className="text-2xl text-center">Get Your Free Quote</CardTitle>
                <CardDescription className="text-center">Our Toronto team will respond with rates within 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+1 (123) 456-7890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Parcel Type</label>
                    <Input
                      name="parcelType"
                      value={formData.parcelType}
                      onChange={handleChange}
                      placeholder="e.g., Documents, Gifts, Relocation"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white h-12 text-lg"
                  >
                    Get Quote Now
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="mt-8 text-center text-white">
              <p className="text-lg mb-4">Or reach out directly:</p>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <a href="tel:+14376160758" className="flex items-center justify-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                  <Phone className="w-5 h-5" />
                  +1 (437) 616-0758
                </a>
                <a href="mailto:ship@shippzy.ca" className="flex items-center justify-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                  <Mail className="w-5 h-5" />
                  ship@shippzy.ca
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA Section */}
        <section className="py-12 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Toronto's Trusted International Courier Service</h3>
            <p className="text-gray-300 mb-6">
              Fast, reliable, and affordable courier services from Toronto to 185+ countries. Compare DHL, FedEx, UPS rates and save up to 75%. Door-to-door delivery with customs clearance support and convenient GTA pickup options.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-3"
                onClick={() => window.location.href = '/partner'}
              >
                Find Drop-Off Location
              </Button>
              <Button 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3"
                onClick={() => window.location.href = '/'}
              >
                View All Services
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default InternationalCourierTorontoPage;